// GAVION ai-email (spec §1): assembles one lead's context for one rep from a `ContextDb` port. Pure (no import with a
// remote specifier), so the demo's FakeRest twin (src/components/gavion/ai/api/fakeAiContext.ts) runs the same code.
// The Supabase port is contextRepo.ts. The full texts of notes and calls, and the meeting/call-note summaries, are read
// by id (v_timeline keeps 280 characters).
import { DEFAULT_TEMPLATES } from "../email/defaultTemplates.ts";
import { decodeEntities, htmlToText } from "../email/htmlToText.ts";
import { isHttpsUrl, renderSubject, renderTemplate } from "../email/render.ts";
import { companyContextFromAi, companyFactsFromAi } from "./companyContext.ts";
import type { ContextInput, DraftTrigger, Lang, TimelineRow } from "./types.ts";

type Row = Record<string, unknown>;
export type TextKind =
  | "company_note"
  | "contact_note"
  | "deal_note"
  | "call"
  | "task";
export type ContextDb = {
  company(id: number): Promise<Row | null>;
  contacts(companyId: number): Promise<Row[]>;
  /**
   * The given deal, else the lead's newest open deal (stage not gagne/perdu), else null; never one of `hidden`.
   * GAVION deal amount visibility (72_): its amount is null when `viewerSalesId` holds only Setter / Apporteur on it.
   */
  deal(
    companyId: number,
    dealId: number | null,
    hidden: readonly number[],
    viewerSalesId?: number,
  ): Promise<Row | null>;
  /**
   * GAVION deal visibility (65_): the ids of the lead's deals the draft's rep may not see (none for an admin; same rule
   * as public.can_see_deal). Their deal and their timeline rows (deal notes, meeting notes) stay out of the draft.
   */
  hiddenDealIds(companyId: number, salesId: number): Promise<number[]>;
  /** v_timeline for the company, newest first, at most 61 rows: the 61st only tells the builder that older history was cut. */
  timeline(companyId: number): Promise<TimelineRow[]>;
  texts(
    kind: TextKind,
    ids: number[],
    companyId: number,
  ): Promise<{ id: number; text: string | null }[]>;
  noteSummaries(
    ids: number[],
    companyId: number,
    hidden: readonly number[], // GAVION deal visibility: notes attached to these deals are left out
  ): Promise<{ id: number; summary: string | null }[]>;
  objections(
    trade: string | null,
  ): Promise<{ objection: string; response: string }[]>;
  /** configuration.config.gavion.ai (product_brief, brochure_url, company_context, company_facts, …). */
  aiConfig(): Promise<Row>;
  /** The call that triggered an after_call draft: its outcome and the e-mail the rep typed for the brochure (calls.email). */
  call(
    id: number,
    companyId: number,
  ): Promise<{ outcome: string | null; email: string | null } | null>;
  /** Owner ruling D5/P4: email_templates slug 'brochure' in that language, not archived (drafts allowed); null if none. */
  brochureTemplate(
    language: Lang,
  ): Promise<{ subject: string; body_html: string } | null>;
  rep(salesId: number): Promise<Row | null>;
  style(salesId: number): Promise<Row | null>;
};
export type ContextRequest = {
  companyId: number;
  contactId: number | null;
  dealId: number | null;
  salesId: number;
  trigger: DraftTrigger;
  instruction: string | null;
  clientMessage: string | null;
  recipientEmail: string | null;
  /** The queued draft's source (ai_email_drafts.source_type / source_id): 'call' + calls.id for an after_call. */
  sourceType: string | null;
  sourceId: number | null;
};

export const DEFAULT_PRODUCT_BRIEF =
  "Gavion répond aux appels des entrepreneurs, qualifie les demandes et prend les rendez-vous à leur place.";
const TEXT_KINDS: readonly TextKind[] = [
  "company_note",
  "contact_note",
  "deal_note",
  "call",
  "task",
];
const EMAIL = /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/;
const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v.trim() : null;
const num = (v: unknown): number | null =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? null
    : Number(v);
const validEmail = (v: unknown): string | null => {
  const s = str(v)?.toLowerCase();
  return s && EMAIL.test(s) ? s : null;
};
const firstEmail = (v: unknown): string | null => {
  if (!Array.isArray(v)) return null;
  for (const e of v) {
    const s = str((e as Row | null)?.email)?.toLowerCase();
    if (s && EMAIL.test(s)) return s;
  }
  return null;
};

const hrefs = (html: string): string[] =>
  [...html.matchAll(/<a\s[^>]*?href\s*=\s*(?:"([^"]*)"|'([^']*)')/gi)].map(
    (m) => decodeEntities(m[1] ?? m[2] ?? "").trim(),
  );

/**
 * Owner ruling D5/P4: the brochure template, rendered for this lead. Its own https link wins; without one, the config
 * brochure URL stands in. The text keeps every link as « texte (url) », so the template's figures and link are trusted.
 */
function renderBrochure(
  row: { subject: string; body_html: string },
  vars: Record<string, unknown>,
  configUrl: string | null,
): NonNullable<ContextInput["brochureTemplate"]> {
  const html = renderTemplate(row.body_html, vars).output;
  return {
    subject: renderSubject(row.subject, vars).output.trim(),
    text: htmlToText(html).trim(),
    url: hrefs(html).find(isHttpsUrl) ?? configUrl,
  };
}

export async function loadContextInput(
  db: ContextDb,
  r: ContextRequest,
): Promise<ContextInput | null> {
  const company = await db.company(r.companyId);
  if (!company) return null;
  const language: Lang = company.language === "en" ? "en" : "fr";
  const hidden = await db.hiddenDealIds(r.companyId, r.salesId); // GAVION deal visibility
  const hiddenIds = new Set(hidden.map(String));
  const [contactRows, deal, timeline, cfg, rep, style] = await Promise.all([
    db.contacts(r.companyId),
    db.deal(r.companyId, r.dealId, hidden, r.salesId), // GAVION (72_): the amount masked for a Setter / Apporteur only
    db.timeline(r.companyId),
    db.aiConfig(),
    db.rep(r.salesId),
    db.style(r.salesId),
  ]);
  const own = timeline.filter(
    (t) =>
      Number(t.company_id) === r.companyId &&
      (t.meta?.deal_id == null || !hiddenIds.has(String(t.meta.deal_id))),
  );
  const fullTexts: Record<string, string> = {};
  for (const kind of TEXT_KINDS) {
    const ids = own.filter((t) => t.kind === kind).map((t) => t.source_id);
    if (ids.length === 0) continue;
    for (const x of await db.texts(kind, ids, r.companyId))
      if (x.text) fullTexts[`${kind}:${x.id}`] = x.text;
  }
  const noteIds = new Set<number>();
  for (const t of own) {
    if (t.kind === "meeting_note") noteIds.add(t.source_id);
    if (t.kind === "call" && Array.isArray(t.meta.meeting_note_ids))
      for (const id of t.meta.meeting_note_ids as unknown[])
        if (num(id) !== null) noteIds.add(Number(id));
  }
  const noteSummaries: Record<number, string> = {};
  if (noteIds.size > 0)
    for (const x of await db.noteSummaries([...noteIds], r.companyId, hidden))
      if (x.summary) noteSummaries[x.id] = x.summary;
  const contacts = contactRows.map((c) => ({
    id: Number(c.id),
    firstName: str(c.first_name),
    lastName: str(c.last_name),
    title: str(c.title),
    email: firstEmail(c.email_jsonb),
    phoneTypes: Array.isArray(c.phone_jsonb)
      ? [
          ...new Set(
            (c.phone_jsonb as Row[])
              .map((p) => str(p?.type))
              .filter((t): t is string => t !== null),
          ),
        ]
      : [],
  }));
  // A draft written for a deal goes to the deal's contact (its first one with an email), not the lead's own address.
  const dealContactIds =
    r.dealId !== null && deal && Array.isArray(deal.contact_ids)
      ? (deal.contact_ids as unknown[]).map(Number)
      : [];
  const contact =
    contacts.find((c) => c.id === r.contactId) ??
    (r.contactId === null
      ? (dealContactIds
          .map((id) => contacts.find((c) => c.id === id && c.email !== null))
          .find((c) => c !== undefined) ?? null)
      : null);
  const brochure = str(cfg.brochure_url);
  const configUrl = brochure && /^https:\/\//.test(brochure) ? brochure : null;
  // Owner ruling D5: only an after_call whose call logged the outcome brochure_sent carries the brochure template.
  const call =
    r.trigger === "after_call" && r.sourceType === "call" && r.sourceId !== null
      ? await db.call(r.sourceId, r.companyId)
      : null;
  const isBrochure = call?.outcome === "brochure_sent";
  let brochureTemplate: ContextInput["brochureTemplate"] = null;
  if (isBrochure) {
    const row =
      (await db.brochureTemplate(language)) ??
      DEFAULT_TEMPLATES.find(
        (t) => t.slug === "brochure" && t.language === language,
      ) ??
      null;
    if (row)
      brochureTemplate = renderBrochure(
        row,
        {
          company: {
            name: str(company.name) ?? "",
            city: str(company.city) ?? "",
            trade_label: str(company.trade) ?? "",
          },
          contact: { first_name: contact?.firstName ?? "" },
          rep: {
            first_name: str(rep?.first_name) ?? "",
            full_name: [str(rep?.first_name), str(rep?.last_name)]
              .filter(Boolean)
              .join(" "),
          },
        },
        configUrl,
      );
  }
  // P3: for brochure_sent the rep typed the address in the outcome form (calls.email); it comes before the contact's.
  const recipient =
    r.recipientEmail?.trim().toLowerCase() ||
    (isBrochure ? validEmail(call?.email) : null) ||
    contact?.email ||
    str(company.email)?.toLowerCase() ||
    null;
  return {
    trigger: r.trigger,
    lead: {
      id: r.companyId,
      name: str(company.name) ?? "?",
      trade: str(company.trade),
      city: str(company.city),
      region: str(company.region),
      language,
      prospectStatus: str(company.prospect_status) ?? "new",
      email: str(company.email)?.toLowerCase() ?? null,
      nextCallbackAt: str(company.next_callback_at),
    },
    contacts,
    deal: deal
      ? {
          id: Number(deal.id),
          name: str(deal.name) ?? "",
          stage: str(deal.stage) ?? "",
          amount: num(deal.amount),
          demoAt: str(deal.demo_at),
        }
      : null,
    recipient: { email: recipient, contactId: contact?.id ?? null },
    timeline: own,
    fullTexts,
    noteSummaries,
    objections: await db.objections(str(company.trade)),
    facts: {
      productBrief: str(cfg.product_brief) ?? DEFAULT_PRODUCT_BRIEF,
      brochureUrl: configUrl,
      companyContext: companyContextFromAi(cfg), // GAVION gavion-context
      companyFacts: companyFactsFromAi(cfg),
    },
    brochureTemplate,
    rep: {
      firstName: str(rep?.first_name) ?? "",
      lastName: str(rep?.last_name),
    },
    style:
      style && str(style.profile_text)
        ? {
            profileText: String(style.profile_text),
            excerpts: Array.isArray(style.excerpts)
              ? (style.excerpts as unknown[])
                  .filter((e): e is string => typeof e === "string")
                  .slice(0, 3)
              : [],
          }
        : null,
    instruction: str(r.instruction),
    clientMessage: str(r.clientMessage),
  };
}
